/**
 * Turn a driver-start/prompt failure into a friendly, actionable message when the
 * cause is a missing/unusable agent binary; otherwise return `null` so the caller
 * surfaces the raw error unchanged.
 *
 * The bridge classifies these as `category: "config"` and carries a `hint`, but the
 * shape reaching the CLI varies: omp's `start()` rejects with the raw spawn error
 * (`code === "ENOENT"`), while the subprocess CLI drivers reject with the classified
 * `AgentError` attached under `.detail`. Duck-type across all of them.
 */
export function friendlyDriverError(err: unknown): string | null {
  const e = err as
    | {
        code?: string;
        syscall?: string;
        message?: string;
        category?: string;
        hint?: string;
        detail?: { category?: string; hint?: string };
      }
    | null
    | undefined;
  if (!e) return null;

  // A missing binary is a *spawn* ENOENT (`syscall: "spawn <bin>"` / "spawn … ENOENT");
  // a bare ENOENT from connector/mount setup is a different failure, not the driver.
  const isSpawnEnoent =
    e.code === "ENOENT" &&
    (e.syscall?.startsWith("spawn") === true || /^spawn .*ENOENT/.test(e.message ?? ""));
  const isConfig = e.category === "config" || e.detail?.category === "config";
  if (!isSpawnEnoent && !isConfig) return null;

  const hint = e.hint ?? e.detail?.hint;
  const base = hint ?? "The configured agent binary is not installed or not on your PATH.";
  return `${base}\nTry a different backend with \`--driver claude-sdk\`.`;
}
